/*
 * Session state for the SPA.
 *
 * The browser has no token to inspect, so "am I logged in" is a question only the
 * BFF can answer — it is asked once on boot (`GET /api/auth/session`, which also
 * performs a silent refresh when the short-lived bearer has lapsed) and then kept
 * in React state. A 401 from any later call collapses the session back to
 * anonymous, which is the app's single logout path besides the button.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  login as loginRequest,
  logout as logoutRequest,
  me,
  meContext,
  probeSession,
  setActiveScope,
  type SessionUser,
} from '../api/client.ts'

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated'

export interface SessionState {
  status: SessionStatus
  user: SessionUser | null
  /** Scope the app acts in; `null` means "the caller's default scope". */
  scopeId: string | null
  scopeLabel: string | null
  error: string | null
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Collapse to anonymous after a 401 from any authenticated call. */
  expire: () => void
}

export function useSession(): SessionState {
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [user, setUser] = useState<SessionUser | null>(null)
  const [scopeId, setScopeId] = useState<string | null>(null)
  const [scopeLabel, setScopeLabel] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * Adopt the caller's default scope so every later request names it explicitly.
   * A single-scope user would work without this — the API falls back to the
   * default when no scope is sent — but naming it makes the scope visible in the
   * UI and exercises the BFF's transport normalisation.
   */
  const adoptScope = useCallback(async () => {
    try {
      const context = await meContext()
      const active = context.default_scope_id ?? context.scope_id ?? null
      setActiveScope(active)
      setScopeId(active)
      const match = context.scopes.find((scope) => scope.scope_id === active)
      setScopeLabel(match?.label ?? null)
    } catch {
      // A missing context is not fatal: with no scope named, the API resolves the
      // caller's default itself.
      setActiveScope(null)
      setScopeId(null)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const probe = await probeSession()
        if (cancelled) return
        if (probe.authenticated) {
          // Identity comes from the API itself, not from the probe: `GET /v1/me`
          // is the caller's own profile resolved from the principal, which is
          // the answer a reload needs and the probe deliberately does not carry.
          try {
            const identity = await me()
            if (cancelled) return
            setUser({
              id: identity.user_id,
              email: identity.email ?? '',
              role: identity.role,
            })
          } catch {
            // A profile that cannot be read is not a dead session; the rail
            // simply has no email to show. A real 401 lands in `expire`.
            setUser(null)
          }
          setStatus('authenticated')
          await adoptScope()
        } else {
          setStatus('anonymous')
        }
      } catch (err) {
        if (cancelled) return
        setStatus('anonymous')
        setError(err instanceof Error ? err.message : 'could not reach the session endpoint')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adoptScope])

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null)
      try {
        const result = await loginRequest(email, password)
        setUser(result.user)
        setStatus('authenticated')
        await adoptScope()
      } catch (err) {
        const message =
          err instanceof ApiError && err.status === 401
            ? 'invalid email or password'
            : err instanceof Error
              ? err.message
              : 'sign-in failed'
        setError(message)
        throw err
      }
    },
    [adoptScope],
  )

  const signOut = useCallback(async () => {
    await logoutRequest().catch(() => undefined)
    setActiveScope(null)
    setUser(null)
    setScopeId(null)
    setScopeLabel(null)
    setStatus('anonymous')
  }, [])

  const expire = useCallback(() => {
    setActiveScope(null)
    setUser(null)
    setStatus('anonymous')
    setError('session expired — sign in again')
  }, [])

  return { status, user, scopeId, scopeLabel, error, signIn, signOut, expire }
}
